import React, { useState, useEffect } from "react";
import { unmountComponentAtNode } from "react-dom";
import { Formik, Form } from "formik";
import { Button } from "primereact/button";
import { FileUpload } from "primereact/fileupload";
import { DefaultImage } from "../../icons";
import { uploadImage, deleteImage, updateImageOrder } from "../../api/ads.api";
import Lightbox from "react-image-lightbox";
import VideoPlayer from "react-video-js-player";

// --- NUEVAS IMPORTACIONES PARA DRAG AND DROP ---
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  useSortable,
  rectSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

import "react-image-lightbox/style.css";
import "./ImagesAds.scss";

// --- NUEVO COMPONENTE PARA LA IMAGEN INDIVIDUAL ARRASTRABLE ---
// Este componente representa cada una de las imágenes que se podrán ordenar.
const SortableImage = ({ url, origin, id, onDelete }) => {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id: id }); // Usamos el hook 'useSortable' de dnd-kit

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    cursor: "grab",
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      className="preview"
    >
      <img
        src={url}
        alt="Otras imágenes"
        width={200}
        height={200}
        // El onClick del lightbox se podría mantener si se desea
      />
      <Button
        type="button"
        icon="pi pi-trash p-ml-auto"
        style={{
          width: "100%",
          background: "#2b363d",
          border: "none",
          borderRadius: "0",
          paddingRight: "5%",
          marginTop: "1%",
        }}
        onClick={() => onDelete(url, origin)} // Llamamos a la función de borrado
      />
    </div>
  );
};

const ImagesAds = ({ id, setActiveIndex, adById }) => {
  const [mainPreview, setMainPreview] = useState("");
  const [blueprintPreview, setBlueprintPreview] = useState([]);
  const [othersPreview, setOthersPreview] = useState([]);
  const [mediaPreview, setMediaPreview] = useState("");
  const [photoIndex, setPhotoIndex] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [images, setImages] = useState([]);
  const [isDeletingOthers, setIsDeletingOthers] = useState(false);
  const [isDeletingBlueprint, setIsDeletingBlueprint] = useState(false);
  const [isUploading, setIsUploading] = useState(false);

  const isUploadedUrl = (url) =>
    url.startsWith("http://") || url.startsWith("https://");

  useEffect(() => {
    if (adById) {
      setMainPreview(adById.images.main);
      // Guardamos un array de objetos con id único para dnd-kit
      setBlueprintPreview(
        adById.images.blueprint.map((url) => ({
          id: url,
          url,
        })),
      );
      setOthersPreview(
        adById.images.others.map((url) => ({
          id: url,
          url,
        })),
      );
      setMediaPreview(adById.images.media);
    }
  }, [adById]);

  // Definimos los sensores para dnd-kit. PointerSensor es bueno para clics y arrastres.
  const sensors = useSensors(
    useSensor(PointerSensor, {
      // Evita que el drag se active con un simple click, requiere un pequeño movimiento.
      activationConstraint: {
        distance: 8,
      },
    }),
  );

  const upload = async (e) => {
    setIsUploading(true);
    let data = new FormData();
    if (
      (e.options.props.name === "others") |
      (e.options.props.name === "blueprint")
    ) {
      for (let file of e.files) {
        data.append(e.options.props.name, file);
      }
    } else {
      data.append(e.options.props.name, e.files[0]);
    }

    try {
      // 1. Esperamos a que la promesa de 'uploadImage' se resuelva
      const res = await uploadImage(id, data, e.options.props.name);

      // 2. Si todo va bien, ejecutamos la lógica de éxito
      alert(`Imagen subida al anuncio ${res.title}`);

      // Actualizamos el estado del frontend con la respuesta del servidor
      if (e.options.props.name === "main") {
        setMainPreview(res.images.main);
      }
      if (e.options.props.name === "blueprint") {
        // Usando la URL como ID para mayor estabilidad
        setBlueprintPreview(
          res.images.blueprint.map((url) => ({
            id: url,
            url,
          })),
        );
      }
      if (e.options.props.name === "others") {
        // Usando la URL como ID para mayor estabilidad
        setOthersPreview(
          res.images.others.map((url) => ({
            id: url,
            url,
          })),
        );
      }
      if (e.options.props.name === "media") {
        setMediaPreview(res.images.media);
      }
    } catch (error) {
      // 3. Si 'uploadImage' falla, el error se captura aquí
      console.error("Error al subir la imagen:", error);
      alert("Hubo un error al subir la imagen. Por favor, inténtalo de nuevo.");
    } finally {
      // 4. Este bloque se ejecuta siempre, tanto si hay éxito como si hay error
      setIsUploading(false); // Reactivamos el botón
      e.options.clear(); // Limpiamos los archivos seleccionados del componente FileUpload
    }
  };

  const deleteImg = (url, from) => {
    let data = { toDelete: url };

    // Lógica para actualizar el estado local inmediatamente
    if (from === "others") {
      setOthersPreview((prev) => prev.filter((img) => img.url !== url));
    }
    if (from === "blueprint") {
      setBlueprintPreview((prev) => prev.filter((img) => img.url !== url));
    }

    deleteImage(id, data, from).then((res) => {
      if (from === "media") {
        unmountComponentAtNode(document.getElementById("video-player"));
        setMediaPreview("");
      }
      alert(`Imagen borrada del anuncio ${res.title}`);
    });
  };

  const handleChangeFile = (e, setter) => {
    let reader = new FileReader();
    reader.onload = () => {
      if (reader.readyState === 2) {
        setter(reader.result);
      }
    };
    reader.readAsDataURL(e);
  };

  const handleChangeFiles = (images, previewField, setter) => {
    if (images) {
      const newImages = images.map((file, index) => ({
        // Creamos un id único para cada imagen nueva
        id: `new-${file.name}-${index}-${Date.now()}`,
        url: file.objectURL,
      }));
      setter([...previewField, ...newImages]);
    }
  };

  const headerTemplate = (options) => {
    const { className, chooseButton, uploadButton } = options;

    const customChooseButton = React.cloneElement(chooseButton, {
      disabled: isUploading, // Se deshabilita si isUploading es true
    });

    const customUploadButton = React.cloneElement(uploadButton, {
      disabled: isUploading || uploadButton.props.disabled, // Se deshabilita si isUploading es true
    });

    return (
      <div
        className={className}
        style={{
          backgroundColor: "transparent",
          display: "flex",
          justifyContent: "right",
        }}
      >
        {customChooseButton}
        {customUploadButton}
      </div>
    );
  };

  const emptyTemplate = () => {
    return (
      <div className="p-d-flex p-ai-left p-flex-wrap p-order-6">
        <div
          style={{
            border: "1px solid lightgrey",
            padding: "45px",
            width: "176px",
            height: "154px",
            margin: "0.5%",
            marginTop: "2.5%",
          }}
        >
          <DefaultImage />
        </div>
      </div>
    );
  };

  const mediaTemplate = () => {
    return mediaPreview ? (
      <div id="video-player" className="preview">
        <VideoPlayer src={mediaPreview} alt="Vídeo" width={284} height={246} />
        <Button
          type="button"
          icon="pi pi-trash p-ml-auto"
          style={{
            width: "100%",
            background: "#2b363d",
            border: "none",
            borderRadius: "0",
            paddingRight: "5%",
            marginTop: "1%",
          }}
          onClick={() => {
            deleteImg(mediaPreview, "media");
          }}
        />
      </div>
    ) : (
      <div
        style={{
          border: "1px solid lightgrey",
          padding: "45px",
          width: "176px",
          height: "154px",
          margin: "0.5%",
          marginTop: "2.5%",
        }}
      >
        <DefaultImage />
      </div>
    );
  };

  // --- FUNCIÓN onDragEnd PARA MANEJAR EL FINAL DEL ARRASTRE Y GUARDAR ---
  const handleDragEnd = (event, setter, origin) => {
    // 'origin' será 'others' o 'blueprint'
    const { active, over } = event;

    // Asegúrate que el elemento se movió a una nueva posición válida
    if (over && active.id !== over.id) {
      let newOrderedArray = [];

      setter((items) => {
        const oldIndex = items.findIndex((item) => item.id === active.id);
        const newIndex = items.findIndex((item) => item.id === over.id);

        // Guardamos el array reordenado para usarlo después
        newOrderedArray = arrayMove(items, oldIndex, newIndex);
        return newOrderedArray;
      });

      // --- ¡AQUÍ ES DONDE USAS TU FUNCIÓN! ---

      // 1. Extrae solo las URLs del array ordenado.
      const orderedUrls = newOrderedArray.map((item) => item.url);

      // 2. Prepara el cuerpo de la petición.
      const dataToSend = {
        from: origin,
        urls: orderedUrls,
      };

      // 3. Llama a la API para guardar el nuevo orden.
      // El 'id' del anuncio ya lo tienes disponible como prop en el componente.
      updateImageOrder(id, dataToSend)
        .then((response) => {
          // Opcional: Notificar al usuario que todo salió bien.
          console.log(`Orden de "${origin}" guardado con éxito.`, response);
        })
        .catch((error) => {
          // ¡Importante! Manejar cualquier error.
          console.error("Error al guardar el nuevo orden:", error);
          alert("No se pudo guardar el nuevo orden. Inténtalo de nuevo.");
        });
    }
  };

  // --- FUNCIÓN `renderMultiple` MODIFICADA PARA SER UN CONTENEDOR ARRASTRABLE ---
  const renderSortableGallery = (source, setter, origin) => {
    return (
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={(event) => handleDragEnd(event, setter, origin)}
      >
        <SortableContext items={source} strategy={rectSortingStrategy}>
          <div className="p-d-flex p-ai-left p-flex-wrap p-order-6">
            {source.map((img) => (
              <SortableImage
                key={img.id}
                id={img.id}
                url={img.url}
                origin={origin}
                onDelete={(url, from) => deleteImg(url, from)}
              />
            ))}
          </div>
        </SortableContext>
      </DndContext>
    );
  };

  const deleteAllImages = (from, previewArray, setter, setLoading) => {
    if (!previewArray.length) return;

    const confirmDelete = window.confirm(
      "¿Estás seguro de que quieres borrar todas las imágenes?",
    );
    if (!confirmDelete) return;

    setLoading(true); // ⏳ Inicia loader

    const urlsToDelete = previewArray.map((img) => img.url);

    deleteImage(id, { toDelete: urlsToDelete }, from)
      .then(() => {
        setter([]);
        alert(`Todas las imágenes de "${from}" han sido borradas.`);
      })
      .catch((err) => {
        console.error("Error al borrar todas las imágenes:", err);
        alert("Ocurrió un error al intentar borrar las imágenes.");
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <Formik
      enableReinitialize={true}
      // Los initialValues ahora deben manejar arrays de objetos {id, url}
      initialValues={{
        main: mainPreview ? mainPreview : "",
        blueprint: blueprintPreview.length !== 0 ? blueprintPreview : [],
        others: othersPreview.length !== 0 ? othersPreview : [],
      }}
    >
      {(formProps) => (
        <Form>
          <div style={{ display: "flex", flexDirection: "column" }}>
            {isOpen && (
              <Lightbox
                mainSrc={images[photoIndex]}
                nextSrc={images[(photoIndex + 1) % images.length]}
                prevSrc={
                  images[(photoIndex + images.length - 1) % images.length]
                }
                onCloseRequest={() => setIsOpen(false)}
                onMovePrevRequest={() =>
                  setPhotoIndex(
                    (photoIndex + images.length - 1) % images.length,
                  )
                }
                onMoveNextRequest={() =>
                  setPhotoIndex((photoIndex + 1) % images.length)
                }
              />
            )}
            <h4
              style={{
                textAlign: "start",
                margin: "10px 0px",
                marginTop: "20px",
                color: "black",
              }}
            >
              Imagen Principal
            </h4>
            <div>
              <div
                style={{
                  display: "flex",
                  justifyContent: "end",
                  borderTop: "1px solid #ccc",
                  paddingBottom: "4rem",
                }}
              >
                <FileUpload
                  name="main"
                  chooseLabel="Cargar imagen principal"
                  uploadHandler={upload}
                  customUpload={true}
                  accept="image/*"
                  maxFileSize={20971520}
                  onRemove={() =>
                    setMainPreview(adById ? adById.images.main : "")
                  }
                  onSelect={(e) => {
                    formProps.setFieldValue("main", e.files[0]);
                    handleChangeFile(e.files[0], setMainPreview);
                  }}
                  headerTemplate={headerTemplate}
                />
              </div>
              {mainPreview ? (
                <div className="preview">
                  <img
                    src={mainPreview}
                    alt="Imagen principal"
                    width={284}
                    height={246}
                    onClick={() => {
                      setIsOpen(true);
                      setImages([mainPreview]);
                    }}
                  />
                  <Button
                    type="button"
                    icon="pi pi-trash p-ml-auto"
                    style={{
                      width: "100%",
                      background: "#2b363d",
                      border: "none",
                      borderRadius: "0",
                      paddingRight: "5%",
                      marginTop: "1%",
                    }}
                    onClick={() => {
                      setMainPreview("");
                      deleteImg(mainPreview, "main");
                    }}
                  />
                </div>
              ) : (
                emptyTemplate()
              )}
            </div>

            <h4
              style={{
                textAlign: "start",
                margin: "10px 0px",
                marginTop: "20px",
                color: "black",
              }}
            >
              Otras imágenes
            </h4>
            <div>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  borderTop: "1px solid #ccc",
                  paddingBottom: "4rem",
                }}
              >
                <div style={{ padding: "12.5px" }}>
                  <Button
                    label={isDeletingOthers ? "Borrando..." : "Borrar todas"}
                    icon={
                      isDeletingOthers ? "pi pi-spin pi-spinner" : "pi pi-trash"
                    }
                    className="p-button-danger"
                    disabled={
                      isDeletingOthers ||
                      othersPreview.filter((img) => isUploadedUrl(img.url))
                        .length === 0
                    }
                    onClick={() =>
                      deleteAllImages(
                        "others",
                        othersPreview.filter((img) => isUploadedUrl(img.url)),
                        setOthersPreview,
                        setIsDeletingOthers,
                      )
                    }
                  />
                </div>
                <FileUpload
                  multiple
                  name="others"
                  chooseLabel="Cargar imágenes"
                  uploadHandler={upload}
                  customUpload={true}
                  onUpload={(props) => props.onClear()}
                  className="p-fileupload"
                  onRemove={(ev) => {
                    // Ajustamos la lógica de borrado para el nuevo formato de estado
                    const newPreview = othersPreview.filter(
                      (preview) => preview.url !== ev.file.objectURL,
                    );
                    setOthersPreview(newPreview);
                  }}
                  onSelect={(ev) => {
                    formProps.setFieldValue("others", ev.files);
                    handleChangeFiles(
                      ev.files,
                      othersPreview,
                      setOthersPreview,
                    );
                  }}
                  accept="image/*"
                  maxFileSize={20971520}
                  headerTemplate={headerTemplate}
                />
              </div>
              {/* Usamos la nueva función para renderizar la galería arrastrable */}
              {othersPreview.length !== 0
                ? renderSortableGallery(
                    othersPreview,
                    setOthersPreview,
                    "others",
                  )
                : emptyTemplate()}
            </div>
            <hr />
            <h4
              style={{
                textAlign: "start",
                margin: "10px 0px",
                marginTop: "20px",
                color: "black",
              }}
            >
              Planos
            </h4>
            <div>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  borderTop: "1px solid #ccc",
                  paddingBottom: "4rem",
                }}
              >
                <div style={{ padding: "12.5px" }}>
                  <Button
                    label={isDeletingBlueprint ? "Borrando..." : "Borrar todas"}
                    icon={
                      isDeletingBlueprint
                        ? "pi pi-spin pi-spinner"
                        : "pi pi-trash"
                    }
                    className="p-button-danger"
                    disabled={
                      isDeletingBlueprint ||
                      blueprintPreview.filter((img) => isUploadedUrl(img.url))
                        .length === 0
                    }
                    onClick={() =>
                      deleteAllImages(
                        "blueprint",
                        blueprintPreview.filter((img) =>
                          isUploadedUrl(img.url),
                        ),
                        setBlueprintPreview,
                        setIsDeletingBlueprint,
                      )
                    }
                  />
                </div>
                <FileUpload
                  multiple
                  name="blueprint"
                  className="p-fileupload"
                  chooseLabel="Cargar plano"
                  uploadHandler={upload}
                  customUpload={true}
                  onUpload={(props) => props.onClear()}
                  accept="image/*"
                  maxFileSize={20971520}
                  onRemove={(ev) => {
                    // Ajustamos la lógica de borrado para el nuevo formato de estado
                    const newPreview = blueprintPreview.filter(
                      (preview) => preview.url !== ev.file.objectURL,
                    );
                    setBlueprintPreview(newPreview);
                  }}
                  onSelect={(ev) => {
                    formProps.setFieldValue("blueprint", ev.files);
                    handleChangeFiles(
                      ev.files,
                      blueprintPreview,
                      setBlueprintPreview,
                    );
                  }}
                  headerTemplate={headerTemplate}
                />
              </div>
              {/* Usamos la nueva función también aquí */}
              {blueprintPreview.length !== 0
                ? renderSortableGallery(
                    blueprintPreview,
                    setBlueprintPreview,
                    "blueprint",
                  )
                : emptyTemplate()}

              <h4
                style={{
                  textAlign: "start",
                  margin: "10px 0px",
                  marginTop: "20px",
                  color: "black",
                }}
              >
                Vídeo
              </h4>
              <div>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "end",
                    borderTop: "1px solid #ccc",
                    paddingBottom: "4rem",
                  }}
                >
                  <FileUpload
                    name="media"
                    chooseLabel="Cargar vídeo"
                    uploadHandler={upload}
                    customUpload={true}
                    accept="video/*"
                    maxFileSize={20971520}
                    onUpload={(props) => props.onClear()}
                    onRemove={() => {
                      setMediaPreview(
                        adById?.images.media ? adById.images.media : "",
                      );
                    }}
                    onSelect={(e) => {
                      formProps.setFieldValue("media", e.files[0]);
                    }}
                    headerTemplate={headerTemplate}
                  />
                </div>
                {mediaTemplate()}
              </div>
            </div>
          </div>
        </Form>
      )}
    </Formik>
  );
};

export default ImagesAds;
